import React, { useEffect, useState } from "react";
import {
  add_cv_against_candidates,
  delete_cv_against_candidates,
  get_candidates_cv_list,
  update_cv_of_candidate,
} from "../../DAL/cv-management/cv-management";
import { enqueueSnackbar } from "notistack";
import CircularLoader from "../../components/loader/CircularLoader";
import { UploadFile } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { get_short_string, getFileIcon } from "../../utils/Constant";
import {
  ArrowDown,
  ArrowUp,
  EyeIcon,
  GripVertical,
  MoveDown,
  MoveUp,
  Trash2,
} from "lucide-react";
import CustomConfirmation from "../../components/popUp/CustomConfirmation";
import UpgradeModal from "../../components/popUp/UpgradeModal";
import { useIds } from "../../hook/AdminContext";
import DocumentModalboxCv from "../DocumentPreview/DocumentModalBoxcv";
import { s3baseUri } from "../../config/config";
import RecordNoImage from "../../components/recordNotFound/RecordNoImage";

export default function CvManagement() {
  const { userInfo } = useIds();

  const [CVList, setCVList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [targetCV, setTargetCV] = useState(null);
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const [openCVDialog, setOpenCVDialog] = useState(false);

  const handleGetCVList = async () => {
    setLoading(true);
    const resp = await get_candidates_cv_list();
    if (resp.code === 200) setCVList(resp.data.cv_list || []);
    else enqueueSnackbar(resp.message, { variant: "error" });
    setLoading(false);
  };

  const handleAgreeDelete = (cvObj) => {
    setTargetCV(cvObj);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    const resp = await delete_cv_against_candidates(targetCV._id);
    if (resp.code === 200) {
      enqueueSnackbar(resp.message, { variant: "success" });
      handleGetCVList();
    } else enqueueSnackbar(resp.message, { variant: "error" });
    setOpenDelete(false);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedExtensions = [".pdf", ".doc", ".docx"];
    const lower = file.name.toLowerCase();
    const isValid = allowedExtensions.some((ext) => lower.endsWith(ext));
    if (!isValid) {
      enqueueSnackbar("Only PDF, DOC, and DOCX files are accepted!", {
        variant: "error",
      });
      return;
    }
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar("Maximum allowed file size is 5MB.", {
        variant: "error",
      });
      return;
    }
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    const resp = await add_cv_against_candidates(formData);
    if (resp.code === 200) handleGetCVList();
    else enqueueSnackbar(resp.message, { variant: "error" });
    setIsUploading(false);
  };

  const handleUpdateOrder = async (order, cv) => {
    setLoading(true);
    const formData = new FormData();

    formData.append("order", order);
    formData.append("default", cv.default_status);

    const resp = await update_cv_of_candidate(cv._id, formData);
    if (resp.code == 200) {
      handleGetCVList();
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setLoading(false);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const cv = CVList.find((obj) => obj._id === result.draggableId);
    handleUpdateOrder(result.destination.index + 1, cv);
    // optionally call API to persist order here
  };

  const handleUp = (index, cv) => {
    if (index === 0) return; // Already at the top
    handleUpdateOrder(index, cv);
  };

  // Move item down
  const handleDown = (index, cv) => {
    if (index === CVList.length) return; // Already at the bottom
    handleUpdateOrder(index + 2, cv);
  };

  useEffect(() => {
    if (userInfo?.profile_id) handleGetCVList();
  }, [userInfo?.profile_id]);

  return (
    <>
      {openDelete && (
        <CustomConfirmation
          openDelete={openDelete}
          setOpenDelete={setOpenDelete}
          title="Are you sure you want to delete this CV?"
          headerHeading="Delete CV"
          handleAgree={handleDelete}
        />
      )}

      {openUpgradeModal && (
        <UpgradeModal
          open={openUpgradeModal}
          handleClose={() => setOpenUpgradeModal(false)}
          islimitExceedModal={true}
        />
      )}

      {openCVDialog && (
        <DocumentModalboxCv
          open={openCVDialog}
          handleClose={() => {
            setOpenCVDialog(false);
          }}
          docName={targetCV.name}
          image_url={
            targetCV instanceof File
              ? URL.createObjectURL(targetCV)
              : s3baseUri + targetCV.path
          }
          previewDoc={userInfo}
        />
      )}
      <div className="container-fluid mt-3">
        <div className="d-flex justify-content-between">
          <div>
            <h2>CV Management</h2>
            <p>
              Streamline the management of your CVs with drag & drop ordering.
            </p>
          </div>

          <div className="d-flex flex-column align-items-end">
            <Tooltip
              title={
                CVList.length === 5 ? "Maximum limit reached. Delete an existing CV to upload more." : ""
              }
              placement="top"
              arrow
            >
              <span>
                <label
                  htmlFor="cv-upload"
                  style={{
                    cursor:
                      CVList.length === 5 || isUploading || loading
                        ? "not-allowed"
                        : "pointer",
                    opacity: CVList.length === 5 ? 0.6 : 1,
                    pointerEvents: CVList.length === 5 ? "none" : "auto",
                  }}
                >
                  <input
                    accept=".pdf,.doc,.docx"
                    id="cv-upload"
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    disabled={CVList.length === 5 || isUploading || loading}
                  />

                  <button
                    type="button"
                    className="btn-contained-sm"
                    disabled={CVList.length === 5 || isUploading || loading}
                    style={{ pointerEvents: "none" }}
                  >
                    {isUploading ? (
                      "Uploading..."
                    ) : (
                      <>
                        <UploadFile fontSize="small" /> Upload CV
                      </>
                    )}
                  </button>
                </label>
              </span>
            </Tooltip>

            <span className="my-1">
              Accepted files: pdf, docx, doc | Max Size: 5 MB
            </span>
          </div>
        </div>

        {loading ? (
          <CircularLoader />
        ) : (
          <>
            {CVList.length > 0 ? (
              <div className="drag-wrapper">
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="cvList" direction="vertical">
                    {(provided) => (
                      <div
                        className="cv-list"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {CVList.map((cv, index) => (
                          <Draggable
                            key={String(cv._id)}
                            draggableId={String(cv._id)}
                            index={index}
                          >
                            {(provided, snapshot) => {
                              // merge the library's inline style so transforms/positions work correctly

                              const combinedStyle = {
                                ...provided.draggableProps.style,
                                width: snapshot.isDragging
                                  ? provided.draggableProps.style?.width ||
                                    "auto"
                                  : "100%",
                              };

                              return (
                                <div
                                  className={`cv-item ${
                                    snapshot.isDragging ? "dragging" : ""
                                  }`}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={combinedStyle}
                                >
                                  <div className="cv-card w-100 mt-2 position-relative tw-flex tw-items-center tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-p-2 tw-bg-white tw-transition tw-duration-150">
                                    <GripVertical size={18} />

                                    <div className="tw-ml-2 d-flex justify-content-between align-items-center w-100">
                                      <div className="d-flex align-items-center gap-2">
                                        {getFileIcon(cv.name)}

                                        <p className="mb-0 word-break">
                                          {cv.name}
                                        </p>
                                      </div>

                                      <div className="d-flex align-items-center gap-2">
                                        <ArrowUp
                                          className={`tw-cursor-pointer ${
                                            index == 0 ? "tw-disabled" : ""
                                          }`}
                                          size={18}
                                          onClick={() => handleUp(index, cv)}
                                        />
                                        <ArrowDown
                                          className={`tw-cursor-pointer ${
                                            index == CVList.length - 1
                                              ? "tw-disabled"
                                              : ""
                                          }`}
                                          size={18}
                                          onClick={() => handleDown(index, cv)}
                                        />
                                        <EyeIcon
                                          className="tw-cursor-pointer"
                                          size={18}
                                          onClick={() => {
                                            setTargetCV(cv);
                                            setOpenCVDialog(true);
                                          }}
                                        />
                                        <Trash2
                                          className="tw-cursor-pointer"
                                          size={18}
                                          onClick={() => handleAgreeDelete(cv)}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            }}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            ) : (
              <>
                <RecordNoImage
                  html={`                 
                      <p>You haven't uploaded any CVs yet.</p>
                      <span>
                        Once you upload your CV, it will appear here. You can
                        use your uploaded CVs to quickly apply for jobs.
                      </span>
                  `}
                />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
